import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { generateTemporaryAccess, getTemporaryAccessPin, type IdCompanyDto } from '@/services/ws-companies/src/main/resources/rest-api';
import { fromDateTimeType } from '@/js/libs/api-helpers';
import { useCountdown } from '@/js/libs/hooks/useCountdown';
import type { ModalStateOpen } from '@/js/libs/hooks/useModalState';
import { useSubscribe } from '@/js/libs/observable';
import { Button, Loader, Modal, Notification } from '@/js/libs/ui/src/V2';
import { NotActive, useRequest } from '@/js/libs/use-request';

import { CopyTooltip } from 'src/components/CopyTooltip.tsx';
import { PinContainer } from './styled.ts';

type GrantAccessModalProps = {
  state: ModalStateOpen;
  companyId: IdCompanyDto;
};

export const GrantAccessModal = memo(function GrantAccessModal({ state, companyId }: GrantAccessModalProps) {
  const [t] = useTranslation(['components', 'common']);

  const generateRequest = useRequest({
    apiMethod: generateTemporaryAccess,
    params: { companyId },
  });
  const isGenerated = useSubscribe(generateRequest.data$, (response) => Boolean(response));

  const getPinRequest = useRequest({
    apiMethod: getTemporaryAccessPin,
    params: isGenerated ? undefined : NotActive,
  });
  const pin = useSubscribe(getPinRequest.data$, (response) => response?.data ?? null);

  const expiresAtMs = useMemo(() => (pin ? (fromDateTimeType(pin.expiresAt)?.valueOf() ?? null) : null), [pin]);
  const { min, sec } = useCountdown(expiresAtMs);

  const generatePending = useSubscribe(generateRequest.pending$);
  const getPinPending = useSubscribe(getPinRequest.pending$);
  const pending = generatePending || getPinPending;

  return (
    <Modal state={state} title={t('components:GRANT_ADMIN_ACCESS')}>
      <Modal.Body>
        {pending && !pin && <Loader cover />}
        {pin && (
          <>
            <Notification body={t('components:GRANT_ADMIN_ACCESS_PROVIDE_HINT')} uncloseable spacer fullWidth />
            <PinContainer data-testid='grant-access-pin'>
              <p>{t('components:GRANT_ADMIN_ACCESS_CODE_VALID', { min, sec })}</p>
              <CopyTooltip value={pin.pin} />
            </PinContainer>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={state.close} dataTestId='grant-access-continue-button'>
          {t('common:CONTINUE')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});
